import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface Differentiator {
  id: number;
  icon: string;
  title: string;
  description: string;
}

const SolutionSection = () => {
  const differentiators: Differentiator[] = [
    {
      id: 1,
      icon: 'UserGroupIcon',
      title: 'Human-Centered Leadership',
      description: 'We prioritize people over technology, ensuring AI adoption enhances rather than disrupts your workforce.'
    },
    {
      id: 2,
      icon: 'AcademicCapIcon',
      title: 'Proven Change Frameworks',
      description: 'Battle-tested methodologies that have successfully guided hundreds of organizations through complex transformations.'
    },
    {
      id: 3,
      icon: 'ChartBarIcon',
      title: 'Measurable Business Impact',
      description: 'Clear ROI tracking with defined success metrics that align AI initiatives with strategic business objectives.'
    },
    {
      id: 4,
      icon: 'ShieldCheckIcon',
      title: 'Risk-Aware Implementation',
      description: 'Comprehensive governance frameworks that balance innovation with ethical AI use and regulatory compliance.'
    }
  ];

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-br from-card to-background">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-success/10 rounded-full mb-4">
            <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-success" />
            <span className="text-sm font-semibold font-body text-success">The Solution</span>
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-bold font-headline text-foreground mb-4">
            What Is AI Change Leaders
          </h2>
          
          <p className="text-lg text-muted-foreground font-body leading-relaxed">
            We are a curated professional alliance specializing in human-centered AI transformation. Our approach combines cutting-edge AI strategy with proven change management execution to bridge the gap between technology investment and organizational adoption.
          </p>
        </div>
        
        <div className="max-w-5xl mx-auto mb-12">
          <div className="bg-white rounded-xl shadow-strong p-8 lg:p-12 border border-border">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold font-headline text-foreground mb-4">
                  Our Core Philosophy
                </h3>
                <p className="text-muted-foreground font-body mb-6 leading-relaxed">
                  AI success is fundamentally a leadership challenge. Technology alone doesn't drive transformation—people do. We make AI transformations succeed by focusing on the critical human factors that determine adoption success.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-success mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-foreground font-body">Practical wisdom over theoretical frameworks</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-success mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-foreground font-body">Execution-focused delivery that produces real results</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-success mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-foreground font-body">Collaborative professional network model</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                {differentiators.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-start gap-4 p-4 bg-card rounded-lg border border-border hover:border-primary/30 transition-all duration-300"
                  >
                    <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name={item.icon as any} size={20} variant="outline" className="text-primary" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold font-headline text-foreground mb-1">
                        {item.title}
                      </h4>
                      <p className="text-xs text-muted-foreground font-body leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SolutionSection;